import { useRef } from 'react';
import { mintKey, moveKeys, resizeKeys } from '@/utils/row-keys';

export interface RowKeys {
  /** One stable key per row, in current list order. Always `length` long. */
  keys: string[];
  /** Record a row appended to the end of the list. */
  append: () => void;
  /** Record a row removed at `index`. */
  remove: (index: number) => void;
  /** Record a row dragged from `from` to `to`. */
  move: (from: number, to: number) => void;
}

/**
 * Stable React keys for a list whose items carry no id.
 *
 * Indexes cannot be used: after a reorder React reuses each DOM node in place
 * and only swaps props, which leaves HeroUI's Autocomplete showing the
 * previous row's material text — it holds input state that `selectedKey`
 * alone does not reset.
 *
 * A ref rather than state: the mutators run inside event handlers that also
 * call the editor's `onChange`, so the framing state update already schedules
 * the re-render that reads the new keys. The render-phase `resizeKeys` is a
 * pure function of `length`, so re-running it (StrictMode double-render) is a
 * no-op — it resyncs only when the list length changed from outside the
 * mutators, which is what happens when a different template is loaded into an
 * already-mounted editor.
 */
export function useRowKeys(length: number): RowKeys {
  const ref = useRef<string[]>([]);
  ref.current = resizeKeys(ref.current, length);

  return {
    keys: ref.current,
    append: () => {
      ref.current = [...ref.current, mintKey()];
    },
    remove: (index: number) => {
      ref.current = ref.current.filter((_, i) => i !== index);
    },
    move: (from: number, to: number) => {
      ref.current = moveKeys(ref.current, from, to);
    },
  };
}
